import React, { useState, useEffect, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useAuth } from "../../../contexts/AuthContext";

const RegionManagementPanel = ({
  regions,
  projects,
  onAddRegion,
  onUpdateRegion,
  onDeleteRegion,
}) => {
  const { user } = useAuth();
  const [isAddingRegion, setIsAddingRegion] = useState(false);
  const [editingRegionId, setEditingRegionId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    project_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Get project from header selection or user's project
  useEffect(() => {
    if (isAddingRegion && !editingRegionId) {
      const selectedProject = localStorage.getItem("selectedProject");
      const projectId = selectedProject && selectedProject !== "all" 
        ? selectedProject 
        : user?.project_id || null;
      setFormData({ name: "", project_id: projectId || "" });
    }
  }, [isAddingRegion, editingRegionId, user]);

  // Listen for project selection changes
  useEffect(() => {
    const updateSelectedProject = () => {
      const project = localStorage.getItem("selectedProject");
      setSelectedProject(project && project !== "all" ? project : null);
    };

    // Initial load
    updateSelectedProject();

    // Listen for storage changes (when project is selected in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === "selectedProject") {
        updateSelectedProject();
      }
    };

    // Listen for custom event (when project is selected in same tab)
    const handleProjectChange = () => {
      updateSelectedProject();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("projectChanged", handleProjectChange);

    // Poll localStorage periodically to catch changes in same tab
    const interval = setInterval(updateSelectedProject, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("projectChanged", handleProjectChange);
      clearInterval(interval);
    };
  }, []);

  // Filter regions based on selected project
  const filteredRegions = useMemo(() => {
    if (!selectedProject) {
      return regions;
    }
    return regions.filter((region) => region.project_id === selectedProject);
  }, [regions, selectedProject]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = "Region name is required";
    }
    if (!formData?.project_id) {
      newErrors.project_id = "Please select a project from the header";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setLoading(true);
        if (editingRegionId) {
          await onUpdateRegion(editingRegionId, formData);
          setEditingRegionId(null);
        } else {
          await onAddRegion(formData);
          setIsAddingRegion(false);
        }
        setFormData({ name: "", project_id: "" });
        setErrors({});
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (regionId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this region? This will also delete all associated clusters, teams, and customers."
      )
    ) {
      try {
        setDeletingId(regionId);
        await onDeleteRegion(regionId);
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleEdit = (region) => {
    setFormData({
      name: region?.name || "",
      project_id: region?.project_id || "",
    });
    setEditingRegionId(region?.id);
    setIsAddingRegion(false);
  };

  const handleCancel = () => {
    setIsAddingRegion(false);
    setEditingRegionId(null);
    setFormData({ name: "", project_id: "" });
    setErrors({});
  };

  const showRegionModal = isAddingRegion || editingRegionId;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">
            Regions
          </h3>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => {
              setIsAddingRegion(!isAddingRegion);
              setEditingRegionId(null);
              setFormData({ name: "", project_id: "" });
              setErrors({});
            }}
            disabled={projects.length === 0}
          >
            Add Region
          </Button>
        </div>
        {projects.length === 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Please create a project first before adding regions.
          </p>
        )}
      </div>

      {/* Add / Edit Region Modal */}
      {showRegionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Building" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {editingRegionId ? "Edit Region" : "Add Region"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Create or update a region within a project.
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                disabled={loading}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!formData?.project_id && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Please select a project from the header dropdown first.
                  </p>
                </div>
              )}
              {formData?.project_id && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Project:</span>{" "}
                    {projects?.find((p) => p?.id === formData?.project_id)?.name || "N/A"}
                  </p>
                </div>
              )}

              <Input
                label="Region Name"
                type="text"
                placeholder="e.g., North Region, South Region"
                value={formData?.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e?.target?.value });
                  if (errors?.name) setErrors({ ...errors, name: "" });
                }}
                error={errors?.name}
                required
              />

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={loading}
                  disabled={loading}
                >
                  {editingRegionId ? "Update Region" : "Create Region"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="divide-y divide-border">
        {filteredRegions?.length > 0 ? (
          filteredRegions.map((region) => {
            const project = projects?.find((p) => p?.id === region?.project_id);
            return (
              <div
                key={region?.id}
                className="bg-card p-4 md:p-6 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="Building" size={20} className="text-primary" />
                      <h4 className="text-base md:text-lg font-semibold text-foreground">
                        {region?.name}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Folder" size={16} />
                      <span>Project: {project?.name || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                      <span>
                        Created:{" "}
                        {new Date(region?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit2"
                      onClick={() => handleEdit(region)}
                      disabled={deletingId === region?.id}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleDelete(region?.id)}
                      loading={deletingId === region?.id}
                      disabled={deletingId === region?.id}
                      className="text-destructive hover:text-destructive"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-8 text-center">
            <Icon
              name="Building"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground">
              {projects.length === 0
                ? "Please create a project first before adding regions."
                : selectedProject
                ? "No regions found for the selected project. Click 'Add Region' to create one."
                : "No regions created yet. Click 'Add Region' to create one."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionManagementPanel;
